import he from 'he';
import fp from 'lodash/fp';
import { businessAdminRole, businessUserRole, externalUserRole, industryAdminRole, industryType as allIndustryType, managerRole, pendingUserRole, publisherRole, systemAdminRole } from '../constants';
import { unfixedFloor, unfixedCeil } from '../utils';
import {superAdminRole, userRolePriorities} from '../constants/user-roles';

export const cpdProgress = (totalCpdProgress) => {
  const totalCpdCategorys = (totalCpdProgress && totalCpdProgress.cpdCategorys) ? totalCpdProgress.cpdCategorys.length : 0;
  const totalCpdPoints = totalCpdProgress.totalCpdPoints;
  const currentCpdPoints = totalCpdProgress.totalValidCpdPoints;
  const progressPercentage = totalCpdProgress.totalProgressPercentage;

  // Capped earned CPD hours that will be used to display for total CPD progress which is based on overall progress
  // of area of advice, so they must be no more than the assigned points.
  const cappedCurrentCpdPoints = totalCpdProgress.totalApprovedCpdPointsCapped;
  const cappedProgressPercentage = totalCpdPoints === 0 ? 0 : unfixedFloor((cappedCurrentCpdPoints / totalCpdPoints) * 100, 1);
  const remainingCpdPoints = totalCpdProgress.shortFall;
  const pendingCpdPoints = totalCpdProgress.totalPendingCpdPoints;
  const shortFall = totalCpdPoints === 0 ? 0 : unfixedCeil(remainingCpdPoints / totalCpdPoints * 100, 2);
  const tasaCpdPoints = totalCpdProgress.totalTasaCpdPoints;
  const riskRating = totalCpdProgress.totalRiskRating;

  return {
    totalCpdCategorys,
    totalCpdPoints,
    currentCpdPoints,
    progressPercentage,
    cappedCurrentCpdPoints,
    cappedProgressPercentage,
    remainingCpdPoints,
    pendingCpdPoints,
    tasaCpdPoints,
    shortFall,
    riskRating
  };
}

const userProperty = (user, propertyPath) => {
  const property = user && fp.get(propertyPath)(user);
  return property ? he.decode(property) : null;
}

/**
 * Get a users first name.
 *
 * Returns the provided users first name.
 */
export const firstName = (user) => userProperty(user, 'firstName');

/**
 * Get a users last name.
 *
 * Returns the provided users last name.
 */
export const lastName = (user) => userProperty(user, 'lastName');

/**
 * Get a users full name.
 *
 * Returns the provided users full name.
 */
export const fullName = (user) => {
  const userFirstName = firstName(user);
  const userLastName = lastName(user);

  return userFirstName || userLastName ?
    `${userFirstName} ${userLastName}`.trim()
    : 'Anonymous';
}

const hasRoleAssignment = (predicate) => (user) => user.roleAssignments && user.roleAssignments.length && predicate(user.roleAssignments);

const hasRelatedBusiness = (user) => user.roleAssignments.filter(
  (role) => role.businessId
).length > 0

export const isPendingUser = (user) => hasRoleAssignment(pendingUserRole)(user);

// Business Training Plan Manager should have all access as the Business Admin.
// There was a bug where if a user is Manager, it is also a Business Admin on the server side, it is now fixed, but there is
// a business rule allows Manager to access everything as the Business Admin, so we relax the rule here for Manager.
export const isBusinessAdmin = (user) => hasRoleAssignment(businessAdminRole)(user) || isManager(user);

// Because of the bug mentioned for isBusinessAdmin, the new isBusinessAdmin function also contain the manager role
// But sometimes we also want to if the user is a business admin but not manager
// so we introduce the new isBusinessAdminOnly function here.
export const isBusinessAdminOnly = (user) => hasRoleAssignment(businessAdminRole)(user);

export const isBusinessUser = (user) => hasRoleAssignment(businessUserRole)(user);

export const isPublisher = (user) => hasRoleAssignment(publisherRole)(user);

export const isSystemOrSuperAdmin = (user) => hasRoleAssignment(systemAdminRole)(user) || hasRoleAssignment(superAdminRole)(user);

export const isSystemAdmin = (user) => hasRoleAssignment(systemAdminRole)(user);

export const isSuperAdmin = (user) => hasRoleAssignment(superAdminRole)(user);

export const isExternalUser = (user) => hasRoleAssignment(externalUserRole)(user);

export const isExternalBusinessUser = (user) => hasRoleAssignment(externalUserRole)(user) && hasRelatedBusiness(user);

export const isIndustryAdmin = (user) => hasRoleAssignment(industryAdminRole)(user);

export const isManager = (user) => hasRoleAssignment(managerRole)(user);

export const isTeamManager = (user, business) => business && fp.find(role => role.isTeamManager && role.businessId === business.id)(fp.get('teamMemberRoles')(user));

export const getManagedTeam = (user) => fp.flow(fp.get('teamMemberRoles'), fp.find(role => role.isTeamManager), fp.get('businessTeamId'))(user);

export const getPriorityRole = (roles) => roles.map(r => userRolePriorities[r.role]).sort((r1, r2) => r1.priority - r2.priority)[0]

export const isMinimumBased = fp.flow(
  fp.filter(
    ({ industryType }) => industryType === allIndustryType.Minimum
  ),
  (list) => list.length > 0
);

export const isTotalBased = fp.flow(
  fp.filter(
    ({ industryType }) => industryType === allIndustryType.Total
  ),
  (list) => list.length > 0
);

export const defaultQueryTeamMemberConfig = {
  take: 20
}

export const excludeManagers = (users) => fp.filter((user) => !isManager(user))(users)


// WEBPACK FOOTER //
// ./src/services/user.js